// src/components/dashboard/ImageCard.jsx
import React, { useState, useContext, useEffect} from 'react';
import {
    Box,
    Button,
    Text,
    VStack,
    HStack,
    Image,
    Badge,
    Divider,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    IconButton,
    Skeleton,
    Tooltip,
    Flex,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useToast
} from '@chakra-ui/react';
import {
    FiDownload,
    FiEye,
    FiEyeOff,
    FiRefreshCw,
    FiTrash2,
    FiColumns,
    FiBook,
    FiMoreVertical,
    FiCheck,
} from 'react-icons/fi';
import SideBySideViewer from './SideBySideViewer';
import { downloadWithWhiteBackground } from '../../utils/imageUtils';

// Importação do contexto (descomente quando implementar o AlbumContext)
// import { AlbumContext } from '../../contexts/AlbumContext';

const ImageCard = ({ image: initialImage, onDownload, onDelete, refreshImageUrl }) => {
    const [showProcessed, setShowProcessed] = useState(true);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isCompareOpen,
        onOpen: onCompareOpen,
        onClose: onCompareClose
    } = useDisclosure();
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgError, setImgError] = useState(false);
    const [image, setImage] = useState(initialImage);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const toast = useToast();

    // Adicione um novo estado para rastrear se a imagem está no livro
    const [isInAlbum, setIsInAlbum] = useState(false);

    // Se você estiver usando o AlbumContext
    // const albumContext = useContext(AlbumContext);

    // Adicione um useEffect para verificar inicialmente se a imagem já está no livro
    useEffect(() => {
        const checkIfInAlbum = () => {
            try {
                const selectedImages = JSON.parse(localStorage.getItem('albumImages') || '[]');
                const exists = selectedImages.some(img => img.id === image.id);
                setIsInAlbum(exists);
            } catch (error) {
                console.error('Erro ao verificar status no álbum:', error);
            }
        };

        checkIfInAlbum();
    }, [image.id]);

    // Modifique a função addToAlbum para atualizar o estado
    const addToAlbum = () => {
        try {
            // Armazenar a imagem selecionada no localStorage
            const selectedImages = JSON.parse(localStorage.getItem('albumImages') || '[]');

            // Verificar se a imagem já foi adicionada
            const imageExists = selectedImages.some(img => img.id === image.id);

            if (!imageExists) {
                // Adicionar a imagem ao array
                selectedImages.push({
                    id: image.id,
                    title: image.title || `Imagem ${selectedImages.length + 1}`,
                    generated_image_url: image.generated_image_url,
                    original_image_url: image.original_image_url,
                    created_at: image.created_at || new Date().toISOString()
                });

                // Salvar o array atualizado no localStorage
                localStorage.setItem('albumImages', JSON.stringify(selectedImages));

                // Atualizar o estado
                setIsInAlbum(true);

                // Mostrar mensagem de sucesso
                toast({
                    title: "Imagem adicionada",
                    description: "Imagem adicionada ao seu livro de colorir",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
            } else {
                // Informar que a imagem já foi adicionada
                toast({
                    title: "Imagem já adicionada",
                    description: "Esta imagem já está no seu livro de colorir",
                    status: "info",
                    duration: 2000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Erro ao adicionar imagem ao álbum:', error);

            toast({
                title: "Erro ao adicionar imagem",
                description: "Não foi possível adicionar a imagem ao livro",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Função para alternar entre imagem original e processada
    const toggleImage = () => {
        setImgLoaded(false);
        setShowProcessed(!showProcessed);
    };

    // Função para lidar com erro no carregamento da imagem
    const handleImageError = async () => {
        if (isRefreshing) return; // Evitar múltiplas chamadas
        console.error(`Erro ao carregar imagem: ${showProcessed ? image.generated_image_url : image.original_image_url}`);
        setImgError(true);
    };

    return (
        <Box
            borderRadius="lg"
            overflow="hidden"
            bg="white"
            boxShadow="md"
            transition="all 0.3s"
            _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
            position="relative"
        >
            {/* Menu de opções no canto superior direito */}
            <Menu>
                <MenuButton
                    as={IconButton}
                    icon={<FiMoreVertical />}
                    variant="ghost"
                    size="sm"
                    position="absolute"
                    top="8px"
                    right="8px"
                    zIndex="1"
                    color="white"
                    _hover={{ bg: "blackAlpha.300" }}
                />
                <MenuList>
                    <MenuItem
                        icon={<FiBook color={isInAlbum ? "green" : undefined} />}
                        onClick={addToAlbum}
                        color={isInAlbum ? "green.500" : undefined}
                        fontWeight={isInAlbum ? "bold" : "normal"}
                    >
                        {isInAlbum ? "Já está no Livro ✓" : "Adicionar ao Livro"}
                    </MenuItem>
                </MenuList>
            </Menu>

            <Box position="relative" h="200px">
                {/* Container da imagem atual */}
                <Box position="relative" width="100%" height="100%">
                    {!imgLoaded && !imgError && (
                        <Skeleton
                            height="100%"
                            width="100%"
                            position="absolute"
                            startColor="gray.100"
                            endColor="gray.300"
                            borderRadius="md"
                        />
                    )}

                    {imgError ? (
                        <Flex
                            height="100%"
                            width="100%"
                            bg="gray.100"
                            color="gray.500"
                            align="center"
                            justify="center"
                            direction="column"
                            p={4}
                        >
                            <Text>Não foi possível carregar a imagem</Text>
                            <Text fontSize="sm">Verifique as permissões de acesso</Text>
                        </Flex>
                    ) : (
                        <Image
                            src={showProcessed ? image.generated_image_url : image.original_image_url}
                            alt={showProcessed ? "Desenho para colorir" : "Imagem original"}
                            objectFit="cover"
                            w="100%"
                            h="100%"
                            onLoad={() => setImgLoaded(true)}
                            onError={handleImageError}
                            style={{ opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
                        />
                    )}

                    {/* NOVO: Badge de livro quando adicionada */}
                    {isInAlbum && (
                        <Badge
                            position="absolute"
                            bottom="10px"
                            right="10px"
                            colorScheme="purple"
                            borderRadius="full"
                            px={2}
                            boxShadow="md"
                        >
                            No Livro ✓
                        </Badge>
                    )}
                </Box>
            </Box>

            <Box p={4}>
                <Text fontWeight="medium" mb={1} noOfLines={1}>
                    {image.title || "Desenho para colorir"}
                </Text>
                <Text fontSize="sm" color="gray.500" mb={3}>
                    {image.created_at ? new Date(image.created_at).toLocaleDateString() : ""}
                </Text>

                <Divider mb={3} />

                {/* Layout principal de botões */}
                <VStack spacing={3} width="100%" align="stretch">
                    {/* Botão de alternar - sempre visível com texto completo */}
                    <Button
                        leftIcon={showProcessed ? <FiEye /> : <FiEyeOff />}
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        onClick={toggleImage}
                        width="100%"
                    >
                        Alternar para {showProcessed ? "Original" : "Processada"}
                    </Button>

                    {/* Correção para os botões de ação no desktop */}
                    <Box width="100%">
                        {/* Versão Desktop: botões em 2x2 */}
                        <VStack
                            spacing={2}
                            display={{ base: "none", md: "flex" }}
                            width="100%"
                            align="stretch"
                        >
                            {/* Primeira linha - Comparar e Baixar */}
                            <HStack spacing={2} width="100%">
                                <Button
                                    leftIcon={<FiColumns />}
                                    size="sm"
                                    variant="outline"
                                    colorScheme="teal"
                                    onClick={onCompareOpen}
                                    flex="1"
                                >
                                    Comparar
                                </Button>
                                <Button
                                    leftIcon={<FiDownload />}
                                    size="sm"
                                    colorScheme="green"
                                    onClick={() => downloadWithWhiteBackground(
                                        showProcessed ? image.generated_image_url : image.original_image_url,
                                        `${image.title || 'imagem'}_${showProcessed ? 'colorir' : 'original'}.jpg`
                                    )}
                                    flex="1"
                                >
                                    Baixar
                                </Button>
                            </HStack>

                            {/* Segunda linha - Livro e Excluir */}
                            <HStack spacing={2} width="100%">
                                <Button
                                    leftIcon={isInAlbum ? <FiCheck /> : <FiBook />}
                                    size="sm"
                                    colorScheme={isInAlbum ? "purple" : "purple"}
                                    variant={isInAlbum ? "solid" : "outline"}
                                    onClick={addToAlbum}
                                    flex="1"
                                >
                                    {isInAlbum ? "No Livro ✓" : "Livro"}
                                </Button>

                                {onDelete && (
                                    <Button
                                        leftIcon={<FiTrash2 />}
                                        size="sm"
                                        colorScheme="red"
                                        variant="outline"
                                        onClick={onDelete}
                                        flex="1"
                                    >
                                        Excluir
                                    </Button>
                                )}
                            </HStack>
                        </VStack>

                        {/* Versão Mobile: botões ícones sem texto (mantém como estava) */}
                        <Flex
                            justify="space-between"
                            width="100%"
                            gap={2}
                            display={{ base: "flex", md: "none" }}
                        >
                            <Tooltip label="Comparar lado a lado" hasArrow>
                                <IconButton
                                    icon={<FiColumns />}
                                    aria-label="Comparar lado a lado"
                                    onClick={onCompareOpen}
                                    variant="outline"
                                    colorScheme="teal"
                                    size="sm"
                                    flex="1"
                                />
                            </Tooltip>

                            <Tooltip label="Baixar imagem" hasArrow>
                                <IconButton
                                    icon={<FiDownload />}
                                    aria-label="Baixar imagem"
                                    onClick={() => downloadWithWhiteBackground(
                                        showProcessed ? image.generated_image_url : image.original_image_url,
                                        `${image.title || 'imagem'}_${showProcessed ? 'colorir' : 'original'}.jpg`
                                    )}
                                    colorScheme="green"
                                    size="sm"
                                    flex="1"
                                />
                            </Tooltip>

                            <Tooltip label={isInAlbum ? "Já está no Livro" : "Adicionar ao Livro"} hasArrow>
                                <IconButton
                                    icon={isInAlbum ? <FiCheck /> : <FiBook />}
                                    aria-label="Adicionar ao Livro"
                                    onClick={addToAlbum}
                                    colorScheme={isInAlbum ? "purple" : "purple"}
                                    variant={isInAlbum ? "solid" : "outline"}
                                    size="sm"
                                    flex="1"
                                />
                            </Tooltip>

                            {/* Botão de exclusão */}
                            {onDelete && (
                                <Tooltip label="Excluir imagem" hasArrow>
                                    <IconButton
                                        icon={<FiTrash2 />}
                                        aria-label="Excluir imagem"
                                        onClick={onDelete}
                                        colorScheme="red"
                                        variant="outline"
                                        size="sm"
                                        flex="1"
                                    />
                                </Tooltip>
                            )}
                        </Flex>
                    </Box>
                </VStack>
            </Box>

            {/* Modal para visualização ampliada */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack spacing={2}>
                            <Text>{image.title || "Imagem"}</Text>
                            <Badge colorScheme={showProcessed ? "purple" : "blue"}>
                                {showProcessed ? "Processada" : "Original"}
                            </Badge>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <Box position="relative">
                            {imgError ? (
                                <Flex
                                    height="300px"
                                    width="100%"
                                    bg="gray.100"
                                    color="gray.500"
                                    align="center"
                                    justify="center"
                                    direction="column"
                                    p={4}
                                    borderRadius="md"
                                >
                                    <Text>Não foi possível carregar a imagem</Text>
                                    <Text fontSize="sm">Verifique as permissões de acesso</Text>
                                </Flex>
                            ) : (
                                <Image
                                    src={showProcessed ? image.generated_image_url : image.original_image_url}
                                    alt={showProcessed ? "Desenho para colorir" : "Imagem original"}
                                    w="100%"
                                    borderRadius="md"
                                />
                            )}
                        </Box>

                        <HStack mt={4} spacing={4} justify="center" flexWrap="wrap">
                            <Button
                                leftIcon={<FiRefreshCw />}
                                onClick={toggleImage}
                                m={1}
                            >
                                Alternar para {showProcessed ? "Original" : "Processada"}
                            </Button>
                            <Button
                                leftIcon={<FiDownload />}
                                colorScheme="green"
                                onClick={() => onDownload(
                                    showProcessed ? image.generated_image_url : image.original_image_url,
                                    `${image.title || 'imagem'}_${showProcessed ? 'colorir' : 'original'}.png`
                                )}
                                m={1}
                            >
                                Baixar Imagem
                            </Button>
                            <Button
                                leftIcon={<FiColumns />}
                                colorScheme="teal"
                                onClick={() => {
                                    onClose();
                                    onCompareOpen();
                                }}
                                m={1}
                            >
                                Comparar Lado a Lado
                            </Button>

                            {/* Botão para adicionar ao álbum no modal */}
                            <Button
                                leftIcon={isInAlbum ? <FiCheck /> : <FiBook />}
                                colorScheme={isInAlbum ? "green" : "purple"}
                                onClick={addToAlbum}
                                m={1}
                            >
                                {isInAlbum ? "Já está no Livro ✓" : "Adicionar ao Livro"}
                            </Button>

                            {/* Botão de exclusão no modal */}
                            {onDelete && (
                                <Button
                                    leftIcon={<FiTrash2 />}
                                    colorScheme="red"
                                    variant="outline"
                                    onClick={() => {
                                        onClose();
                                        onDelete();
                                    }}
                                    m={1}
                                >
                                    Excluir
                                </Button>
                            )}
                        </HStack>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Modal de comparação lado a lado */}
            <SideBySideViewer
                isOpen={isCompareOpen}
                onClose={onCompareClose}
                originalImage={image.original_image_url}
                processedImage={image.generated_image_url}
                title={image.title || "Comparação"}
                onDownload={onDownload}
                onDelete={onDelete}  // Passando a função de exclusão para o visualizador lado a lado
            />
        </Box>
    );
};

export default ImageCard;